import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Info, Loader2, RefreshCw, ScrollText, Trash2 } from 'lucide-react';
import { Button } from '../Button';
import { PlotFehler } from '../../services/plotter/plotTypen';
import {
    PLOT_ROLLE_KNAPP_MM, PLOT_ROLLE_STANDARD_MM, meterText, plotDruckNachtragLoeschen, plotDruckUebersichtLaden,
    plotRollenStandLaden,
    type PlotDruckUebersicht, type PlotDruckZeile, type PlotDruckZeilenStatus, type PlotRolle, type PlotRollenStand,
} from '../../services/plotter/plotRolle';
import { PlotRolleErfassen } from './PlotRolleErfassen';

/**
 * Abschnitt „Plotter: Papierrolle und Druckliste“ der globalen Einstellungen
 * (SQL Nr. 169/170): Stand der eingelegten Rolle, alle gezählten Drucke im
 * Zeitraum mit Länge und die Gesamtlänge — für den Nachkauf (Rollen à 20 m).
 * Gezählt wird jeder Plotter-Auftrag, SiGe-Plan wie Vorankündigung — die
 * Überschrift nennt deshalb den Plotter, nicht den SiGe-Plan.
 *
 * Gezählt wird nur auf dem Server (`plot_rolle_rechnen`); hier wird nichts
 * nachgerechnet. Nachträge (alter Plan direkt vom Rechner gedruckt) darf der
 * Admin löschen — Rückfrage inline, kein Browser-Dialog. Geladen wird erst
 * beim Aufklappen.
 */

type Zeitraum = 'seit_rolle' | 'monat' | 'letzter_monat' | 'jahr' | 'alle';

const ZEITRAEUME: { wert: Zeitraum; text: string }[] = [
    { wert: 'seit_rolle', text: 'Seit Rollenwechsel' },
    { wert: 'monat', text: 'Dieser Monat' },
    { wert: 'letzter_monat', text: 'Letzter Monat' },
    { wert: 'jahr', text: 'Dieses Jahr' },
    { wert: 'alle', text: 'Alle' },
];

/** Die Übersicht rechnet über viele Zeilen — etwas mehr Zeit als eine Liste. */
const LADEN_FRIST_MS = 20_000;
const MAX_RUNDEN = 3;

const mitFrist = <T,>(p: Promise<T>): Promise<T> => new Promise<T>((ok, nein) => {
    const t = setTimeout(() => nein(new PlotFehler('netz', 'Keine Antwort vom Server — bitte erneut aktualisieren.')), LADEN_FRIST_MS);
    p.then(w => { clearTimeout(t); ok(w); }, (e: unknown) => { clearTimeout(t); nein(e); });
});

const fehlerText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

// ─── Zeit (Europe/Berlin) ────────────────────────────────────────────────────

const BERLIN = 'Europe/Berlin';

const berlinTeile = new Intl.DateTimeFormat('en-CA', {
    timeZone: BERLIN, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

/** Kalenderangaben in Berlin zum Zeitpunkt `ms`. */
const berlinZeit = (ms: number) => {
    const t: Record<string, number> = {};
    for (const p of berlinTeile.formatToParts(new Date(ms))) if (p.type !== 'literal') t[p.type] = Number(p.value);
    return { jahr: t.year, monat: t.month, tag: t.day, stunde: t.hour % 24, minute: t.minute, sekunde: t.second };
};

/** Berliner Ortszeit minus UTC (ms) zum Zeitpunkt `ms` — 1 h im Winter, 2 h im Sommer. */
const berlinVersatzMs = (ms: number): number => {
    const z = berlinZeit(ms);
    return Date.UTC(z.jahr, z.monat - 1, z.tag, z.stunde, z.minute, z.sekunde) - Math.floor(ms / 1000) * 1000;
};

/**
 * Mitternacht am Ersten des Monats in Berlin als ISO-Zeitpunkt. `monat`
 * 1-basiert und darf über- oder unterlaufen (0 = Dezember des Vorjahres).
 * Die Zeitumstellung liegt nie um Mitternacht; die zweite Runde fängt den
 * Monat ab, in dem sie liegt.
 */
const berlinMonatsanfang = (jahr: number, monat: number): string => {
    const utc = Date.UTC(jahr, monat - 1, 1);
    let t = utc - berlinVersatzMs(utc);
    t = utc - berlinVersatzMs(t);
    return new Date(t).toISOString();
};

/** `von` inklusiv, `bis` exklusiv; null = offen. */
const zeitraumGrenzen = (z: Zeitraum, rolle: PlotRolle | null, jetztMs: number): { von: string | null; bis: string | null } => {
    const { jahr, monat } = berlinZeit(jetztMs);
    switch (z) {
        case 'seit_rolle':
            // Ohne Rolle gibt es keinen Wechsel — dann der laufende Monat.
            return { von: rolle ? rolle.eingelegt_am : berlinMonatsanfang(jahr, monat), bis: null };
        case 'monat':
            return { von: berlinMonatsanfang(jahr, monat), bis: null };
        case 'letzter_monat':
            return { von: berlinMonatsanfang(jahr, monat - 1), bis: berlinMonatsanfang(jahr, monat) };
        case 'jahr':
            return { von: berlinMonatsanfang(jahr, 1), bis: null };
        default:
            return { von: null, bis: null };
    }
};

const zeitMs = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const t = Date.parse(iso.trim().replace(' ', 'T'));
    return Number.isFinite(t) ? t : null;
};

const datumFormat = new Intl.DateTimeFormat('de-DE', { timeZone: BERLIN, day: '2-digit', month: '2-digit', year: 'numeric' });
const zeitFormat = new Intl.DateTimeFormat('de-DE', { timeZone: BERLIN, hour: '2-digit', minute: '2-digit' });

const datumUhr = (iso: string | null | undefined): string => {
    const t = zeitMs(iso);
    return t === null ? (iso ?? '') : `${datumFormat.format(t)} ${zeitFormat.format(t)}`;
};
const uhrzeit = (ms: number) => new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Anzeige eines Zeitraums; `bis` ist exklusiv, gezeigt wird der Tag davor. */
const zeitraumText = (von: string | null, bis: string | null): string => {
    const bisMs = zeitMs(bis);
    const ende = bisMs === null ? 'heute' : datumFormat.format(bisMs - 1);
    return von ? `${datumUhr(von)} – ${ende}` : `alle Drucke bis ${ende}`;
};

// ─── Anzeige ─────────────────────────────────────────────────────────────────

const mm = (n: number) => Math.round(n).toLocaleString('de-DE');
const meterZahl = (n: number) => (Math.round(n) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 3 });

const STATUS: Record<PlotDruckZeilenStatus, { text: string; farbe: string }> = {
    gedruckt: { text: 'gedruckt', farbe: 'bg-emerald-100 text-emerald-700' },
    fehler: { text: 'Fehler', farbe: 'bg-red-100 text-red-700' },
    druckt: { text: 'druckt', farbe: 'bg-blue-100 text-blue-700' },
    unklar: { text: 'unklar', farbe: 'bg-amber-100 text-amber-800' },
    nachgetragen: { text: 'nachgetragen', farbe: 'bg-violet-100 text-violet-700' },
};

const projektText = (d: PlotDruckZeile): string =>
    [d.projekt_nummer, d.projekt_name].map(t => (t ?? '').trim()).filter(Boolean).join(' · ') || d.titel;

const blattText = (d: PlotDruckZeile): string =>
    d.breite_mm !== null && d.breite_mm > 0 ? `${mm(d.breite_mm)} × ${mm(d.laenge_mm)} mm` : `${mm(d.laenge_mm)} mm`;

const kopienText = (d: PlotDruckZeile): string =>
    d.kopien_gezaehlt === d.kopien ? String(d.kopien) : `${d.kopien_gezaehlt} (bestellt ${d.kopien})`;

const TH = 'px-3 py-2 text-left text-[11px] font-bold text-slate-500 uppercase whitespace-nowrap';
const TD = 'px-3 py-2 align-top';

export const PlotterPapierVerwaltung: React.FC = () => {
    const [offen, setOffen] = useState(false);
    const [zeitraum, setZeitraum] = useState<Zeitraum>('seit_rolle');
    // undefined = noch nicht geladen; null = SQL Nr. 169/170 fehlt.
    const [daten, setDaten] = useState<PlotDruckUebersicht | null | undefined>(undefined);
    // Mit welchen Grenzen `daten` geladen wurde — für die Zeitraumzeile.
    const [geladen, setGeladen] = useState<{ von: string | null; bis: string | null } | null>(null);
    const [laedt, setLaedt] = useState(false);
    const [ladeFehler, setLadeFehler] = useState('');
    const [standMs, setStandMs] = useState(0);
    const [verlaufOffen, setVerlaufOffen] = useState(false);

    const [loeschenFrage, setLoeschenFrage] = useState<string | null>(null);
    const [loescht, setLoescht] = useState<string | null>(null);
    const [loeschenFehler, setLoeschenFehler] = useState<{ id: string; text: string } | null>(null);
    // Gelöschte Nachträge bis zum nächsten Laden ausblenden.
    const [geloescht, setGeloescht] = useState<ReadonlySet<string>>(new Set());

    const aktivRef = useRef(true);
    const laedtRef = useRef(false);
    const nochmalRef = useRef(false);
    // Zählt abgeschlossene Änderungen (Rolle erfasst, Nachtrag gelöscht): eine
    // Antwort, deren Laden vorher begann, zeigt den alten Stand und wird verworfen.
    const standRef = useRef(0);
    const zeitraumRef = useRef<Zeitraum>('seit_rolle');
    // Zuletzt bekannte aktuelle Rolle; undefined = noch unbekannt.
    const rolleRef = useRef<PlotRolle | null | undefined>(undefined);
    const loeschtRef = useRef(false);

    useEffect(() => {
        aktivRef.current = true;
        return () => { aktivRef.current = false; };
    }, []);

    /**
     * Eine Übersicht für `z`. „Seit Rollenwechsel“ braucht den Beginn der
     * aktuellen Rolle: unbekannt → erst der Stand; hat ein anderer Admin
     * inzwischen eine neue Rolle erfasst, gleich noch einmal mit ihrem Beginn.
     */
    const holen = async (z: Zeitraum) => {
        let rolle = rolleRef.current;
        if (z === 'seit_rolle' && rolle === undefined) {
            const st = await mitFrist(plotRollenStandLaden());
            if (st === null) return { u: null, von: null, bis: null };
            rolle = st.rolle;
        }
        let g = zeitraumGrenzen(z, rolle ?? null, Date.now());
        let u = await mitFrist(plotDruckUebersichtLaden(g.von, g.bis));
        if (u && z === 'seit_rolle' && (u.stand.rolle?.eingelegt_am ?? null) !== (rolle?.eingelegt_am ?? null)) {
            g = zeitraumGrenzen(z, u.stand.rolle, Date.now());
            u = await mitFrist(plotDruckUebersichtLaden(g.von, g.bis));
        }
        return { u, ...g };
    };

    const laden = async () => {
        // Kein zweiter Ladevorgang daneben; ein Wunsch währenddessen wird danach erfüllt.
        if (laedtRef.current) { nochmalRef.current = true; return; }
        laedtRef.current = true;
        setLaedt(true);
        setLadeFehler('');
        try {
            let runden = 0;
            do {
                runden += 1;
                nochmalRef.current = false;
                const stand = standRef.current;
                const z = zeitraumRef.current;
                let ergebnis: Awaited<ReturnType<typeof holen>> | null = null;
                let fehler = '';
                try {
                    ergebnis = await holen(z);
                } catch (e) {
                    fehler = fehlerText(e);
                }
                if (!aktivRef.current) return;
                if (stand !== standRef.current || z !== zeitraumRef.current) {
                    nochmalRef.current = true;
                } else if (fehler || !ergebnis) {
                    setLadeFehler(fehler);
                } else {
                    rolleRef.current = ergebnis.u ? ergebnis.u.stand.rolle : undefined;
                    setDaten(ergebnis.u);
                    setGeladen({ von: ergebnis.von, bis: ergebnis.bis });
                    setGeloescht(new Set());
                    setStandMs(Date.now());
                    setLadeFehler('');
                }
            } while (nochmalRef.current && aktivRef.current && runden < MAX_RUNDEN);
        } finally {
            laedtRef.current = false;
            if (aktivRef.current) setLaedt(false);
        }
    };

    /** Nach einer Änderung: ältere Antworten verwerfen, frisch laden. */
    const nachAenderungLaden = () => {
        standRef.current += 1;
        void laden();
    };

    const umschalten = () => {
        const wirdOffen = !offen;
        setOffen(wirdOffen);
        if (wirdOffen && daten === undefined) void laden();
    };

    const zeitraumWaehlen = (z: Zeitraum) => {
        if (z === zeitraumRef.current) return;
        zeitraumRef.current = z;
        setZeitraum(z);
        void laden();
    };

    const rolleGespeichert = (neu: PlotRollenStand) => {
        rolleRef.current = neu.rolle;
        setDaten(d => (d ? { ...d, stand: neu } : d));
        nachAenderungLaden();
    };

    const loeschen = async (id: string) => {
        if (loeschtRef.current) return;
        loeschtRef.current = true;
        setLoescht(id);
        setLoeschenFehler(null);
        try {
            await plotDruckNachtragLoeschen(id);
            if (!aktivRef.current) return;
            setLoeschenFrage(null);
            setGeloescht(alt => new Set(alt).add(id));
            nachAenderungLaden();
        } catch (e) {
            if (aktivRef.current) setLoeschenFehler({ id, text: fehlerText(e) });
        } finally {
            loeschtRef.current = false;
            if (aktivRef.current) setLoescht(null);
        }
    };

    const stand = daten?.stand ?? null;
    const rolle = stand?.rolle ?? null;
    const rest = stand?.rest_mm ?? null;
    const drucke = (daten?.drucke ?? []).filter(d => !(d.quelle === 'nachtrag' && geloescht.has(d.id)));
    const standardMm = stand?.standard_laenge_mm || PLOT_ROLLE_STANDARD_MM;

    const kopfZeile = daten === undefined
        ? 'Rollenstand und Drucke mit Länge — für den Nachkauf'
        : daten === null
            ? 'Noch nicht eingerichtet'
            : rolle && rest !== null
                ? `Rest ${meterText(rest)} · ${daten.drucke.length} Drucke im Zeitraum`
                : `Noch keine Rolle erfasst · ${daten.drucke.length} Drucke im Zeitraum`;

    return (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-6 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={umschalten}>
                <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-teal-50 text-teal-600 rounded-lg"><ScrollText className="w-5 h-5" /></div>
                    <div className="min-w-0">
                        <h4 className="text-base font-bold text-slate-800">Plotter: Papierrolle und Druckliste</h4>
                        <p className="text-xs text-slate-500">{kopfZeile}</p>
                    </div>
                </div>
                <div className="text-slate-400">{offen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</div>
            </div>
            {offen && (
                <div className="p-6 pt-0 border-t border-slate-100 bg-white">
                    <div className="space-y-5 mt-4">
                        <div className="flex items-start gap-3 bg-teal-50 p-3 rounded-xl border border-teal-100">
                            <Info className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-teal-900 leading-relaxed">
                                Je Kopie zählt die Blattlänge — für jeden Druck aus der App, SiGe-Plan wie Vorankündigung. Reicht die
                                erfasste Rolle für einen Druck nicht mehr, sperrt die App das Senden an den Plotter — für alle, auch für
                                Admins —, bis hier eine neue Rolle erfasst ist. Direkt vom Rechner gedruckte Pläne werden im SiGe-Plan über
                                „Druck nachtragen“ mitgezählt.
                            </p>
                        </div>

                        {ladeFehler && (
                            <div className="flex flex-wrap items-center gap-3">
                                <p className="text-xs text-red-700 flex-1 min-w-[12rem]">Übersicht nicht geladen: {ladeFehler}</p>
                                <Button variant="secondary" size="sm" onClick={() => { void laden(); }} disabled={laedt}>Erneut versuchen</Button>
                            </div>
                        )}

                        {daten === undefined && !ladeFehler && (
                            <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt …</p>
                        )}

                        {daten === null && (
                            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                                SQL Nr. 169/170 fehlt in dieser Umgebung.
                            </p>
                        )}

                        {daten && stand && (
                            <>
                                <div className="flex flex-wrap items-center justify-end gap-1">
                                    {(laedt || standMs > 0) && (
                                        <span className="text-[11px] text-slate-400 tabular-nums" aria-live="polite">
                                            {laedt ? 'Wird aktualisiert …' : `Stand ${uhrzeit(standMs)}`}
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => { void laden(); }}
                                        disabled={laedt}
                                        aria-label="Aktualisieren"
                                        title="Aktualisieren"
                                        className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:text-teal-600 disabled:hover:bg-transparent"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>

                                <RollenStand stand={stand} onGespeichert={rolleGespeichert} />

                                <div className="space-y-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase">Drucke</label>
                                        <div role="group" aria-label="Zeitraum" className="flex flex-wrap gap-1">
                                            {ZEITRAEUME.map(z => (
                                                <button
                                                    key={z.wert}
                                                    type="button"
                                                    onClick={() => zeitraumWaehlen(z.wert)}
                                                    aria-pressed={zeitraum === z.wert}
                                                    className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
                                                        zeitraum === z.wert
                                                            ? 'bg-teal-600 border-teal-600 text-white'
                                                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    {z.text}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {geladen && <p className="text-[11px] text-slate-500">Zeitraum: {zeitraumText(geladen.von, geladen.bis)}</p>}

                                    {drucke.length === 0 ? (
                                        <p className="text-xs text-slate-400">Im Zeitraum wurde nichts gedruckt.</p>
                                    ) : (
                                        // Quelle und Wer stehen unter dem Datum, Titel und Bemerkung
                                        // unter dem Projekt: so passt die Liste neben die Sidebar.
                                        // Erst auf dem Handy scrollt sie in ihrem Rahmen, nie die Seite.
                                        <div className="max-w-full overflow-x-auto max-h-[32rem] overflow-y-auto overscroll-contain border border-slate-200 rounded-xl">
                                            <table className="min-w-[40rem] w-full text-xs text-slate-700">
                                                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                                                    <tr>
                                                        <th className={TH}>Datum</th>
                                                        <th className={TH}>Projekt / Titel</th>
                                                        <th className={TH}>Blatt</th>
                                                        <th className={`${TH} text-right`}>Kopien</th>
                                                        <th className={`${TH} text-right`}>Summe</th>
                                                        <th className={TH}>Status</th>
                                                        <th className={TH}><span className="sr-only">Aktion</span></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {drucke.map(d => (
                                                        <DruckZeile
                                                            key={`${d.quelle}:${d.id}`}
                                                            d={d}
                                                            frage={loeschenFrage === d.id && d.quelle === 'nachtrag'}
                                                            loescht={loescht === d.id}
                                                            fehler={loeschenFehler?.id === d.id ? loeschenFehler.text : ''}
                                                            onFrage={() => { setLoeschenFehler(null); setLoeschenFrage(d.id); }}
                                                            onAbbrechen={() => setLoeschenFrage(null)}
                                                            onLoeschen={() => { void loeschen(d.id); }}
                                                        />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <p className="text-sm font-semibold text-slate-800">
                                        Gesamtlänge im Zeitraum: {meterText(daten.summe_mm)}{' '}
                                        <span className="font-normal text-slate-500">
                                            (≈ {(daten.summe_mm / standardMm).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Rollen à {meterZahl(standardMm)} m)
                                        </span>
                                    </p>
                                    {daten.gekappt && (
                                        <p className="text-xs text-amber-800">
                                            Es sind mehr Drucke im Zeitraum, als die Liste zeigt — sie zeigt die neuesten {mm(daten.drucke.length)} Drucke.
                                            Die Gesamtlänge enthält alle.
                                        </p>
                                    )}
                                </div>

                                {daten.rollen.length > 0 && (
                                    <div>
                                        <button
                                            type="button"
                                            onClick={() => setVerlaufOffen(v => !v)}
                                            aria-expanded={verlaufOffen}
                                            className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase hover:text-slate-700"
                                        >
                                            {verlaufOffen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                            Rollenverlauf ({daten.rollen.length})
                                        </button>
                                        {verlaufOffen && (
                                            <div className="mt-2 max-w-full overflow-x-auto border border-slate-200 rounded-xl">
                                                <table className="min-w-[40rem] w-full text-xs text-slate-700">
                                                    <thead className="bg-slate-50 border-b border-slate-200">
                                                        <tr>
                                                            <th className={TH}>Art</th>
                                                            <th className={TH}>Eingelegt</th>
                                                            <th className={TH}>Bis</th>
                                                            <th className={`${TH} text-right`}>Länge</th>
                                                            <th className={`${TH} text-right`}>Verbraucht</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {daten.rollen.map(r => (
                                                            <tr key={r.id}>
                                                                <td className={TD}>
                                                                    {r.art === 'neu' ? 'Neue Rolle' : 'Korrektur'}
                                                                    {r.bemerkung && <span className="block text-[11px] text-slate-400 break-words">{r.bemerkung}</span>}
                                                                </td>
                                                                <td className={`${TD} whitespace-nowrap`}>
                                                                    {datumUhr(r.eingelegt_am)}
                                                                    {r.erstellt_von_name && <span className="block text-[11px] text-slate-400">{r.erstellt_von_name}</span>}
                                                                </td>
                                                                <td className={`${TD} whitespace-nowrap`}>{r.bis ? datumUhr(r.bis) : 'aktuell'}</td>
                                                                <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{meterText(r.laenge_mm)}</td>
                                                                <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{meterText(r.verbraucht_mm)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const RollenStand: React.FC<{ stand: PlotRollenStand; onGespeichert: (neu: PlotRollenStand) => void }> = ({ stand, onGespeichert }) => {
    const rolle = stand.rolle;
    const rest = stand.rest_mm;
    return (
        <div className="space-y-3">
            <label className="block text-[11px] font-bold text-slate-400 uppercase">Eingelegte Rolle</label>
            {rolle && rest !== null ? (() => {
                const knapp = rest < PLOT_ROLLE_KNAPP_MM;
                const anteil = rolle.laenge_mm > 0 ? Math.min(100, Math.max(0, (rest / rolle.laenge_mm) * 100)) : 0;
                return (
                    <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                        <p className="text-xs text-slate-600">
                            {rolle.art === 'neu' ? 'Neue Rolle' : 'Restlänge erfasst'} am {datumUhr(rolle.eingelegt_am)}
                            {rolle.erstellt_von_name ? ` von ${rolle.erstellt_von_name}` : ''}
                            {rolle.bemerkung ? ` · ${rolle.bemerkung}` : ''}
                        </p>
                        <div
                            role="progressbar"
                            aria-label="Rest auf der Rolle"
                            aria-valuemin={0}
                            aria-valuemax={Math.round(rolle.laenge_mm)}
                            aria-valuenow={Math.max(0, Math.round(rest))}
                            className="h-3 bg-slate-100 rounded-full overflow-hidden"
                        >
                            <div className={`h-full rounded-full ${knapp ? 'bg-red-500' : 'bg-teal-500'}`} style={{ width: `${anteil}%` }} />
                        </div>
                        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div><dt className="text-slate-400">Länge</dt><dd className="font-semibold text-slate-800 tabular-nums">{meterText(rolle.laenge_mm)}</dd></div>
                            <div><dt className="text-slate-400">Verbraucht</dt><dd className="font-semibold text-slate-800 tabular-nums">{meterText(stand.verbraucht_mm)}</dd></div>
                            <div><dt className="text-slate-400">In Warteschlange</dt><dd className="font-semibold text-slate-800 tabular-nums">{meterText(stand.vorgemerkt_mm)}</dd></div>
                            <div>
                                <dt className="text-slate-400">Rest</dt>
                                <dd className={`font-bold tabular-nums ${knapp ? 'text-red-700' : 'text-slate-800'}`}>{meterText(rest)}</dd>
                            </div>
                        </dl>
                        {stand.vorgemerkt_mm > 0 && stand.frei_mm !== null && (
                            <p className="text-[11px] text-slate-500">
                                Frei für neue Aufträge (Rest abzüglich Warteschlange): {meterText(stand.frei_mm)}
                            </p>
                        )}
                        {knapp && (
                            <p className="text-xs text-red-700">
                                {rest < 0
                                    ? 'Laut Zählung ist die Rolle leer. Liegt noch Papier ein, die Restlänge messen und korrigieren.'
                                    : 'Die Rolle ist fast leer — bitte Nachschub bereitlegen.'}
                            </p>
                        )}
                    </div>
                );
            })() : (
                <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                    Noch keine Rolle erfasst — bis dahin wird nicht gesperrt.
                </p>
            )}
            <PlotRolleErfassen stand={stand} onGespeichert={onGespeichert} />
        </div>
    );
};

interface DruckZeileProps {
    d: PlotDruckZeile;
    frage: boolean;
    loescht: boolean;
    fehler: string;
    onFrage: () => void;
    onAbbrechen: () => void;
    onLoeschen: () => void;
}

const DruckZeile: React.FC<DruckZeileProps> = ({ d, frage, loescht, fehler, onFrage, onAbbrechen, onLoeschen }) => {
    const status = STATUS[d.status] ?? STATUS.unklar;
    const projekt = projektText(d);
    return (
        <>
            <tr className={frage ? 'bg-red-50/40' : ''}>
                <td className={TD}>
                    <span className="block whitespace-nowrap tabular-nums">{datumUhr(d.zeitpunkt)}</span>
                    <span className="block text-[11px] text-slate-400">
                        {d.quelle === 'app' ? 'App' : 'Nachgetragen'}{d.von_name ? ` · ${d.von_name}` : ''}
                    </span>
                </td>
                <td className={`${TD} break-words`}>
                    <span className="block font-medium text-slate-800">{projekt}</span>
                    {/* Ohne Projekt steht der Titel schon oben. */}
                    {d.titel && d.titel !== projekt && <span className="block text-[11px] text-slate-500">{d.titel}</span>}
                    {d.bemerkung && <span className="block text-[11px] text-slate-400 italic">{d.bemerkung}</span>}
                </td>
                <td className={`${TD} whitespace-nowrap tabular-nums`}>{blattText(d)}</td>
                <td className={`${TD} text-right whitespace-nowrap tabular-nums`}>{kopienText(d)}</td>
                <td className={`${TD} text-right whitespace-nowrap tabular-nums font-semibold`}>{meterText(d.gesamt_mm)}</td>
                <td className={TD}><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${status.farbe}`}>{status.text}</span></td>
                <td className={`${TD} text-right`}>
                    {d.quelle === 'nachtrag' && !frage && (
                        <button
                            type="button"
                            onClick={onFrage}
                            aria-label="Nachtrag löschen"
                            title="Nachtrag löschen"
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </td>
            </tr>
            {(frage || fehler) && (
                <tr>
                    <td colSpan={7} className="px-3 pb-3">
                        {frage && (
                            <div role="alert" className="flex flex-wrap items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                                <p className="text-xs text-red-800 flex-1 min-w-[12rem]">
                                    Nachtrag „{d.titel}“ ({meterText(d.gesamt_mm)}) löschen? Er zählt dann nicht mehr gegen die Rolle.
                                </p>
                                <Button variant="ghost" size="sm" onClick={onAbbrechen} disabled={loescht}>Abbrechen</Button>
                                <Button variant="danger" size="sm" onClick={onLoeschen} loading={loescht}>Löschen</Button>
                            </div>
                        )}
                        {fehler && <p role="alert" className="text-xs text-red-700 mt-1">{fehler}</p>}
                    </td>
                </tr>
            )}
        </>
    );
};
